/**
 * FR-10's per-cell variant marker on the heat grid — the first variant marker the grid has had (the
 * shipped per-keyword marker lives on the old `KeywordRowsTable/KeywordDataRow.tsx`). It is shown on
 * a sponsored cell, or on the Both-view sponsored strip, for a day whose sponsored rank came from a
 * sibling variant rather than the hero ASIN.
 *
 * The glyph and the hover copy are the **shared call-out convention** (AUT-443): the lucide
 * `SwatchBook` icon in a round `text-current` button and the hover text `Rank from variant: <ASIN>`,
 * matching `@/components/shared/SiblingRankBadge` so the organic cell and the sponsored cell show one
 * identical marker (AUT-520). Whether this renders at all — and which day's ASIN it names — is the
 * caller's call (`RankHeatCell` reads the per-day `sponsoredRankFromVariant[i]` /
 * `sponsoredRankVariantAsin[i]`); this component is a leaf that always renders when mounted.
 */

import { SwatchBook } from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface RankVariantBadgeProps {
  /** The variant ASIN this day's sponsored rank was read from — named in the hover text. */
  variantAsin: string;
  className?: string;
}

export function RankVariantBadge({ variantAsin, className }: RankVariantBadgeProps) {
  const label = `Rank from variant: ${variantAsin}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* `aria-label` carries the hover copy to the accessibility tree (and to a test) without the
            tooltip having to open; `stopPropagation` keeps a click on the marker from reaching the
            keyword row's own click (which opens the detail modal). The round `text-current` button and
            `SwatchBook` glyph mirror `SiblingRankBadge` exactly (AUT-443/AUT-520), so the marker reads
            the same on an organic and a sponsored cell and inherits the cell's ink on any heat colour. */}
        <span
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-current transition-colors hover:bg-foreground/25 cursor-default",
            className,
          )}
        >
          <SwatchBook className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}
